import {
  ArrowPathIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  addAiProjectKnowledgeText,
  AiKnowledgeItem,
  deleteAiProjectKnowledge,
  getAiKnowledgeFileUrl,
  getAiKnowledgeItem,
  updateAiProjectKnowledgeText,
  uploadAiProjectKnowledge,
} from 'services'
import { Button, Input2, Modal, TextArea } from 'stories/components'

const ACCEPTED_FILE_TYPES = '.pdf,.docx,.txt,.md,.csv,.tsv,.json,.xlsx,.xls,.rtf,.html,.xml'

const kb = (chars?: number) => {
  if (!chars) return '0 chars'
  if (chars < 1000) return `${chars} chars`
  return `${Math.round(chars / 1000)}k chars`
}

interface KnowledgePanelProps {
  projectId: number
  items: AiKnowledgeItem[]
  onChanged: () => void
}

/**
 * The project's knowledge base - what the assistant is trained on. Items are files whose text was
 * extracted on upload, or notes typed straight into the project.
 */
export function KnowledgePanel({ projectId, items, onChanged }: KnowledgePanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setUploading] = useState(false)
  const [editing, setEditing] = useState<{ id?: number; name: string; content: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [openingId, setOpeningId] = useState<number | null>(null)

  const onSelectFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length) return
    setUploading(true)
    try {
      const added = await uploadAiProjectKnowledge(projectId, files)
      const unreadable = (added || []).filter((item) => item.status !== 'ready')
      if (unreadable.length) {
        toast(`${unreadable.length} file(s) were stored but their text could not be read.`, { type: 'warning' })
      }
      onChanged()
    } catch {
      // Surfaced by the global API error handler.
    } finally {
      setUploading(false)
    }
  }

  const onEdit = async (item: AiKnowledgeItem) => {
    const full = await getAiKnowledgeItem(projectId, item.id).catch(() => null)
    if (!full) return
    setEditing({ id: full.id, name: full.name, content: full.content || '' })
  }

  const onViewFile = async (item: AiKnowledgeItem) => {
    setOpeningId(item.id)
    try {
      const res = await getAiKnowledgeFileUrl(projectId, item.id)
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer')
      else toast('The original file is not available.', { type: 'error' })
    } finally {
      setOpeningId(null)
    }
  }

  const onSave = async () => {
    if (!editing) return
    if (!editing.name.trim()) {
      toast('Give the note a title.', { type: 'error' })
      return
    }
    setSaving(true)
    try {
      if (editing.id) await updateAiProjectKnowledgeText(projectId, editing.id, editing)
      else await addAiProjectKnowledgeText(projectId, editing.name, editing.content)
      setEditing(null)
      onChanged()
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async (item: AiKnowledgeItem) => {
    if (!window.confirm(`Remove "${item.name}" from this project's knowledge?`)) return
    await deleteAiProjectKnowledge(projectId, item.id)
    onChanged()
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          className="hidden"
          onChange={onSelectFiles}
        />
        <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
          <span className="flex items-center gap-1.5">
            {isUploading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlusIcon className="w-4 h-4" />}
            Upload files
          </span>
        </Button>
        <Button size="sm" color="gray" onClick={() => setEditing({ name: '', content: '' })}>
          <span className="flex items-center gap-1.5">
            <DocumentTextIcon className="w-4 h-4" />
            Add note
          </span>
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {!items.length && (
          <p className="text-sm text-gray-500 p-3">
            Nothing here yet. Upload the documents this project should work from - guidelines, policies,
            templates, spreadsheets - and every conversation in the project will use them.
          </p>
        )}
        {items.map((item) => (
          <div key={item.id} className="border border-gray-200 rounded-md p-2.5 bg-white">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {item.kind === 'text' ? 'Note' : 'File'} · {kb(item.charCount)}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {item.kind === 'file' && (
                  <button
                    className="text-xs text-shade-blue hover:underline disabled:opacity-50"
                    disabled={openingId === item.id}
                    onClick={() => onViewFile(item)}
                  >
                    {openingId === item.id ? 'Opening...' : 'View'}
                  </button>
                )}
                {item.kind === 'text' && (
                  <button
                    className="text-xs text-shade-blue hover:underline"
                    onClick={() => onEdit(item)}
                  >
                    Edit
                  </button>
                )}
                <button
                  className="p-1 text-gray-400 hover:text-red-600"
                  title="Remove"
                  onClick={() => onDelete(item)}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            {item.status !== 'ready' && (
              <p className="mt-1.5 flex items-start gap-1 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                <ExclamationTriangleIcon className="w-3.5 h-3.5 shrink-0 mt-px" />
                <span>{item.error || 'The text of this file could not be read.'}</span>
              </p>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <Modal
          title={editing.id ? 'Edit note' : 'Add note to knowledge'}
          titleOkay="Save"
          isOpen
          loading={saving}
          onOk={onSave}
          onClose={() => setEditing(null)}
        >
          <div className="w-[32rem] max-w-full">
            <Input2
              title="Title"
              required
              value={editing.name}
              onChange={(value: string) => setEditing({ ...editing, name: value })}
            />
            <div className="mt-4">
              <TextArea
                title="Content"
                rows={14}
                value={editing.content}
                onChange={(value: string) => setEditing({ ...editing, content: value })}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
